import React, { useState, useEffect } from 'react';
import { Modal, Box, Button, TextField, FormControl, InputLabel, Select, MenuItem, Typography, IconButton, Collapse, Card, CardContent, Grid, Divider, Snackbar, Alert } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import AddIcon from '@mui/icons-material/Add';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import MDButton from 'components/MDButton';
import { useTheme } from '@mui/material/styles';
import { db, doc, setDoc, updateDoc, onSnapshot, collection, deleteDoc, getDoc } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import { Error as ErrorIcon } from '@mui/icons-material';

// Predefined challenges
const PREDEFINED_CHALLENGES = [
  {
    id: 'toponetrader',
    name: 'TopOneTrader',
    type: 'predefined',
    stages: [
      {
        stage: 1,
        rules: {
          profitTarget: 8,
          maxDailyDrawdown: 5,
          maxOverallDrawdown: 10,
          minTradingDays: 0,
          maxTradingDays: null,
          consistency: 50,
          holdWeekend: true,
        },
      },
      {
        stage: 2,
        rules: {
          profitTarget: 5,
          maxDailyDrawdown: 5,
          maxOverallDrawdown: 10,
          minTradingDays: 0,
          maxTradingDays: null,
          consistency: 50,
          holdWeekend: true,
        },
      },
    ],
  },
  {
    id: 'alpha',
    name: 'Alpha',
    type: 'predefined',
    stages: [
      {
        stage: 1,
        rules: {
          profitTarget: 6,
          maxDailyDrawdown: 3,
          maxOverallDrawdown: 6,
          minTradingDays: 0,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      },
      {
        stage: 2,
        rules: {
          profitTarget: 6,
          maxDailyDrawdown: 3,
          maxOverallDrawdown: 6,
          minTradingDays: 0,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      },
    ],
  },
  {
    id: 'ftmo',
    name: 'FTMO',
    type: 'predefined',
    stages: [
      {
        stage: 1,
        rules: {
          profitTarget: 10,
          maxDailyDrawdown: 5,
          maxOverallDrawdown: 10,
          minTradingDays: 4,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      },
      {
        stage: 2,
        rules: {
          profitTarget: 5,
          maxDailyDrawdown: 5,
          maxOverallDrawdown: 10,
          minTradingDays: 4,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      },
    ],
  },
  {
    id: 'equityedge',
    name: 'EquityEdge',
    type: 'predefined',
    stages: [
      {
        stage: 1,
        rules: {
          profitTarget: 10,
          maxDailyDrawdown: 5,
          maxOverallDrawdown: 10,
          minTradingDays: 2,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      },
      {
        stage: 2,
        rules: {
          profitTarget: 5,
          maxDailyDrawdown: 5,
          maxOverallDrawdown: 10,
          minTradingDays: 2,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      },
    ],
  },
];

const PropFirmChallenge = ({
  userID,
  currentSessionId,
  tradeHistory,
  accountBalance,
  unrealizedPnL,
  currentPlaybackTime,
  isPanelOpen,
  setSelectedChallenges,
  openChallengeModal,
  onCloseChallengeModal
}) => {
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? '#ffffff' : '#344767';
  const [openModal, setOpenModal] = useState(false);
  const [selectedChallenges, setLocalSelectedChallenges] = useState([]);
  const [customChallenge, setCustomChallenge] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [stageDialog, setStageDialog] = useState({ open: false, challengeId: null, nextStage: null });
  const [challengeMetrics, setChallengeMetrics] = useState({});
  const [trades, setTrades] = useState([]);

  useEffect(() => {
    if (currentSessionId && userID) {
      const challengesRef = collection(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`);
      const unsubscribeChallenges = onSnapshot(challengesRef, (snapshot) => {
        const challenges = snapshot.docs.map(doc => {
          const data = doc.data();
          const stagesWithDefaults = data.stages.map(stage => ({
            ...stage,
            rules: {
              ...stage.rules,
              profitTarget: stage.rules.profitTarget || 0,
              maxDailyDrawdown: stage.rules.maxDailyDrawdown || 0,
              maxOverallDrawdown: stage.rules.maxOverallDrawdown || 0,
              minTradingDays: stage.rules.minTradingDays || 0,
              maxTradingDays: stage.rules.maxTradingDays || null,
              consistency: stage.rules.consistency || null,
              holdWeekend: stage.rules.holdWeekend !== undefined ? stage.rules.holdWeekend : true,
            },
          }));
          return { id: doc.id, ...data, stages: stagesWithDefaults };
        });
        setLocalSelectedChallenges(challenges);
        setSelectedChallenges(challenges);
      }, (error) => {
        console.error('Error fetching challenges:', error);
        setSnackbar({ open: true, message: 'Failed to fetch challenges', severity: 'error' });
      });

      const tradesRef = collection(db, `Users/${userID}/backtesting/${currentSessionId}/trades`);
      const unsubscribeTrades = onSnapshot(tradesRef, (snapshot) => {
        const tradeData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Trades from Firebase:', tradeData);
        setTrades(tradeData);
      }, (error) => {
        console.error('Error in trades snapshot:', error);
        setSnackbar({ open: true, message: 'Failed to fetch trades', severity: 'error' });
      });

      return () => {
        unsubscribeChallenges();
        unsubscribeTrades();
      };
    }
  }, [currentSessionId, userID, setSelectedChallenges]);

  useEffect(() => {
    setOpenModal(openChallengeModal); // Sync with parent state
  }, [openChallengeModal]);

  useEffect(() => {
    console.log('Recalculating metrics with:', { trades, tradeHistory, unrealizedPnL, accountBalance });
    const newMetrics = {};
    selectedChallenges.forEach(challenge => {
      const metrics = calculateMetrics(challenge);
      newMetrics[challenge.id] = metrics;
  
      const currentStage = challenge.currentStage || 1;
      const stageRules = challenge.stages.find(s => s.stage === currentStage)?.rules || {};
      const isProfitTargetMet = metrics.profitTarget >= stageRules.profitTarget;
      const isDailyDrawdownMet = Math.abs(metrics.dailyDrawdown) <= stageRules.maxDailyDrawdown;
      const isOverallDrawdownMet = Math.abs(metrics.overallDrawdown) <= stageRules.maxOverallDrawdown;
      const isMinTradingDaysMet = metrics.tradingDays >= stageRules.minTradingDays;
      const isMaxTradingDaysMet = stageRules.maxTradingDays === null || metrics.tradingDays <= stageRules.maxTradingDays;
      const isConsistencyMet = stageRules.consistency === null || metrics.consistency <= stageRules.consistency;
      const isStageComplete = isProfitTargetMet && isDailyDrawdownMet && isOverallDrawdownMet && isMinTradingDaysMet && isMaxTradingDaysMet && isConsistencyMet;
  
      // Check for failure conditions
      const isFailed = !isDailyDrawdownMet || !isOverallDrawdownMet;
  
      if (isFailed && challenge.status !== 'failed' && challenge.status !== 'completed') {
        console.log(`Challenge ${challenge.id} failed: updating status to failed`);
        const challengeRef = doc(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`, challenge.id);
        updateDoc(challengeRef, {
          status: 'failed',
          failedAt: new Date().toISOString()
        }).then(() => {
          setSnackbar({ open: true, message: `Challenge ${challenge.name} failed due to excessive drawdown.`, severity: 'error' });
        }).catch(error => {
          console.error('Error marking challenge as failed:', error);
          setSnackbar({ open: true, message: 'Failed to mark challenge as failed', severity: 'error' });
        });
      } else if (isStageComplete && currentStage === challenge.stages.length && challenge.status !== 'completed') {
        console.log(`Challenge ${challenge.id} completed: updating status to completed`);
        const challengeRef = doc(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`, challenge.id);
        updateDoc(challengeRef, {
          status: 'completed',
          completedAt: new Date().toISOString()
        }).then(() => {
          setSnackbar({ open: true, message: `Congratulations! You have passed the ${challenge.name} challenge!`, severity: 'success' });
        }).catch(error => {
          console.error('Error marking challenge as completed:', error);
          setSnackbar({ open: true, message: 'Failed to mark challenge as completed', severity: 'error' });
        });
      }
    });
    setChallengeMetrics(newMetrics);
  }, [trades, tradeHistory, unrealizedPnL, accountBalance, selectedChallenges, userID, currentSessionId]);

  const handleChallengeSelect = async (challenge) => {
    const challengesRef = collection(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`);
    try {
      const initialStageStartTime = Number.isFinite(currentPlaybackTime) && currentPlaybackTime > 0 
        ? new Date(currentPlaybackTime).toISOString()
        : new Date().toISOString();
      const stagesData = [{
        stage: 1,
        startTime: initialStageStartTime,
        startingBalance: accountBalance,
      }];
      await setDoc(doc(challengesRef, challenge.id), { 
        ...challenge, 
        currentStage: 1,
        stagesData,
        status: 'active'
      });
      setOpenModal(false);
      onCloseChallengeModal();
      setSnackbar({ open: true, message: 'Challenge selected successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error selecting challenge:', error);
      setSnackbar({ open: true, message: 'Failed to select challenge', severity: 'error' });
    }
  };

  const handleAddCustomChallenge = () => {
    setCustomChallenge({
      id: `custom_${uuidv4()}`,
      name: '',
      type: 'custom',
      stages: [{
        stage: 1,
        rules: {
          profitTarget: 0,
          maxDailyDrawdown: 0,
          maxOverallDrawdown: 0,
          minTradingDays: 0,
          maxTradingDays: null,
          consistency: null,
          holdWeekend: true,
        },
      }],
    });
  };

  const handleCustomChallengeChange = (field, value) => {
    setCustomChallenge(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomStageChange = (index, field, value) => {
    setCustomChallenge(prev => {
      const newStages = [...prev.stages];
      newStages[index] = {
        ...newStages[index],
        rules: {
          ...newStages[index].rules,
          [field]: field === 'holdWeekend' ? value === 'true' : parseFloat(value) || null,
        },
      };
      return { ...prev, stages: newStages };
    });
  };

  const handleSaveCustomChallenge = async () => {
    if (customChallenge && customChallenge.name) {
      const challengesRef = collection(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`);
      try {
        const initialStageStartTime = Number.isFinite(currentPlaybackTime) && currentPlaybackTime > 0 
          ? new Date(currentPlaybackTime).toISOString()
          : new Date().toISOString();
        await setDoc(doc(challengesRef, customChallenge.id), { 
          ...customChallenge, 
          currentStage: 1,
          stagesData: [{
            stage: 1,
            startTime: initialStageStartTime,
            startingBalance: accountBalance,
          }],
          status: 'active'
        });
        setCustomChallenge(null);
        setOpenModal(false);
        onCloseChallengeModal();
        setSnackbar({ open: true, message: 'Custom challenge saved!', severity: 'success' });
      } catch (error) {
        console.error('Error saving custom challenge:', error);
        setSnackbar({ open: true, message: 'Failed to save custom challenge', severity: 'error' });
      }
    } else {
      setSnackbar({ open: true, message: 'Please provide a valid challenge name.', severity: 'error' });
    }
  };

  const handleRemoveChallenge = async (challengeId) => {
    const challengeRef = doc(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`, challengeId);
    try {
      await deleteDoc(challengeRef);
      setLocalSelectedChallenges(prev => prev.filter(ch => ch.id !== challengeId));
      setSelectedChallenges(prev => prev.filter(ch => ch.id !== challengeId));
      setSnackbar({ open: true, message: 'Challenge deleted!', severity: 'success' });
    } catch (error) {
      console.error('Error deleting challenge:', error);
      setSnackbar({ open: true, message: 'Failed to delete challenge', severity: 'error' });
    }
  };

  const calculateMetrics = (challenge) => {
    const currentStage = challenge.currentStage || 1;
    const stageData = challenge.stagesData?.find(s => s.stage === currentStage) || {};
    const stageStartTime = stageData.startTime;
    const startingBalance = stageData.startingBalance || accountBalance;

    const metrics = {
      profitTarget: 0,
      dailyDrawdown: 0,
      overallDrawdown: 0,
      tradingDays: 0,
      consistency: 0,
    };

    if (!stageStartTime) {
      console.warn(`No stageStartTime for challenge ${challenge.id}`);
      return metrics;
    }

    const tradeData = trades.length > 0 ? trades : tradeHistory || [];
    if (!tradeData.length) {
      console.log(`No trades available for challenge ${challenge.id}`);
      return metrics;
    }

    // Filter trades for the current stage based on exitDate > stageStartTime (strictly after)
    const stageTrades = tradeData.filter(trade => {
      const isValid = trade.status === 'Closed' && 
                      trade.exitDate && 
                      new Date(trade.exitDate).getTime() > new Date(stageStartTime).getTime();
      console.log(`Trade: ${trade.id}, exitDate: ${trade.exitDate}, stageStartTime: ${stageStartTime}, Included: ${isValid}`);
      return isValid;
    });

    // Calculate profit for the stage
    const stageProfit = stageTrades.reduce((sum, trade) => sum + (trade.profitLoss || 0), 0);
    metrics.profitTarget = (stageProfit / startingBalance) * 100;

    // Calculate overall drawdown
    let runningBalance = startingBalance;
    let minBalance = startingBalance;
    stageTrades.forEach(trade => {
      runningBalance += trade.profitLoss || 0;
      minBalance = Math.min(minBalance, runningBalance);
    });
    metrics.overallDrawdown = ((startingBalance - minBalance) / startingBalance) * 100;

    // Calculate daily drawdown
    const dailyProfits = {};
    stageTrades.forEach(trade => {
      const date = trade.exitDate ? new Date(trade.exitDate).toDateString() : null;
      if (date) {
        dailyProfits[date] = (dailyProfits[date] || 0) + (trade.profitLoss || 0);
      }
    });
    const dailyDrawdowns = Object.values(dailyProfits).map(profit => (profit / startingBalance) * 100);
    metrics.dailyDrawdown = dailyDrawdowns.length > 0 ? Math.min(...dailyDrawdowns, 0) : 0;

    // Trading days
    const uniqueTradingDays = new Set(stageTrades.map(trade => 
      trade.exitDate ? new Date(trade.exitDate).toDateString() : null
    ).filter(Boolean));
    metrics.tradingDays = uniqueTradingDays.size;

    // Consistency
    const stageRules = challenge.stages.find(s => s.stage === currentStage)?.rules || {};
    if (stageRules.consistency) {
      const profits = stageTrades.map(trade => trade.profitLoss || 0).filter(p => p > 0);
      const maxProfit = profits.length > 0 ? Math.max(...profits) : 0;
      const totalPositiveProfit = profits.reduce((sum, p) => sum + p, 0);
      metrics.consistency = totalPositiveProfit > 0 ? (maxProfit / totalPositiveProfit) * 100 : 0;
    }

    console.log(`Metrics for challenge ${challenge.id}, stage ${currentStage}:`, metrics);
    return metrics;
  };

  const handleStageTransition = async (challengeId, nextStage) => {
    const challengeRef = doc(db, `Users/${userID}/backtesting/${currentSessionId}/challenges`, challengeId);
    try {
      const challengeSnap = await getDoc(challengeRef);
      const challengeData = challengeSnap.data();
      const stagesData = challengeData.stagesData || [];
      const sessionRef = doc(db, 'Users', userID, 'backtesting', currentSessionId);
      const sessionSnap = await getDoc(sessionRef);
      const sessionData = sessionSnap.data();
      const fallbackTime = sessionData.startDateMillis || Date.now();

      // Check if nextStage exceeds available stages
      if (!challengeData.stages.find(s => s.stage === nextStage)) {
        console.log(`Challenge ${challengeId} has no stage ${nextStage}. Marking as completed.`);
        await updateDoc(challengeRef, { 
          status: 'completed',
          completedAt: new Date().toISOString()
        });
        setStageDialog({ open: false, challengeId: null, nextStage: null });
        setSnackbar({ open: true, message: `Congratulations! You have passed the ${challengeData.name} challenge!`, severity: 'success' });
        return;
      }

      // Use the latest trade's exitDate as the startTime for the new stage
      let timestamp = null;
      const tradeData = trades.length > 0 ? trades : tradeHistory || [];
      if (tradeData.length > 0) {
        const latestTrade = tradeData
          .filter(trade => trade.status === 'Closed' && trade.exitDate)
          .sort((a, b) => new Date(b.exitDate).getTime() - new Date(a.exitDate).getTime())[0];
        if (latestTrade && latestTrade.exitDate) {
          timestamp = new Date(latestTrade.exitDate).getTime();
          console.log(`handleStageTransition - Using latest trade exitDate: ${timestamp}, ISO: ${new Date(timestamp).toISOString()}, Trade ID: ${latestTrade.id}`);
        }
      }

      // Fallback to currentPlaybackTime if no valid trade exitDate
      if (!timestamp && Number.isFinite(currentPlaybackTime) && currentPlaybackTime > sessionData.startDateMillis) {
        timestamp = currentPlaybackTime;
        console.log(`handleStageTransition - Using currentPlaybackTime: ${timestamp}`);
      }

      // Final fallback to session start
      if (!timestamp) {
        timestamp = fallbackTime;
        console.warn(`handleStageTransition - Fell back to session start: ${timestamp}`);
      }

      console.log(`handleStageTransition - Trades considered:`, tradeData.map(t => ({ id: t.id, exitDate: t.exitDate })));

      const stageStartTime = new Date(timestamp).toISOString();
      stagesData.push({ stage: nextStage, startTime: stageStartTime, startingBalance: accountBalance });
      await updateDoc(challengeRef, { 
        currentStage: nextStage, 
        stagesData 
      });
      setStageDialog({ open: false, challengeId: null, nextStage: null });
      setSnackbar({ open: true, message: `Transitioned to stage ${nextStage}`, severity: 'success' });
    } catch (error) {
      console.error('Error transitioning stage:', error);
      setSnackbar({ open: true, message: 'Failed to transition stage', severity: 'error' });
    }
  };

  const renderSelectionModal = () => (
    <Modal open={openModal} onClose={() => {setOpenModal(false); onCloseChallengeModal();}} sx={{ zIndex: 1300 }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: theme.palette.background.default,
          borderRadius: '12px',
          boxShadow: 1,
          p: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <MDTypography variant="h6" mb={2}>
          Select a Prop Firm Challenge
        </MDTypography>
        <Grid container spacing={2}>
          {PREDEFINED_CHALLENGES.map(challenge => (
            <Grid item xs={12} key={challenge.id}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)',
                  },
                }}
                onClick={() => handleChallengeSelect(challenge)}
              >
                <MDTypography variant="body2" fontWeight="medium">
                  {challenge.name}
                </MDTypography>
                <MDTypography variant="caption">
                  {challenge.type} - Stage {challenge.stages[0].stage}
                </MDTypography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Divider sx={{ my: 2, bgcolor: theme.palette.divider }} />
        <MDTypography variant="h6" mb={2}>
          Create Custom Challenge
        </MDTypography>
        {customChallenge && (
          <Box>
            <TextField
              fullWidth
              label="Challenge Name"
              value={customChallenge.name}
              onChange={(e) => handleCustomChallengeChange('name', e.target.value)}
              sx={{ mb: 2 }}
              InputLabelProps={{ style: { color: iconColor } }}
              InputProps={{ style: { color: iconColor } }}
            />
            {customChallenge.stages.map((stage, index) => (
              <Box key={index} mb={2}>
                <MDTypography variant="body2" sx={{ mb: 1 }}>
                  Stage {stage.stage}
                </MDTypography>
                <TextField
                  fullWidth
                  label="Profit Target (%)"
                  type="number"
                  value={stage.rules.profitTarget || ''}
                  onChange={(e) => handleCustomStageChange(index, 'profitTarget', e.target.value)}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ style: { color: iconColor } }}
                  InputProps={{ style: { color: iconColor } }}
                />
                <TextField
                  fullWidth
                  label="Daily Drawdown (%)"
                  type="number"
                  value={stage.rules.maxDailyDrawdown || ''}
                  onChange={(e) => handleCustomStageChange(index, 'maxDailyDrawdown', e.target.value)}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ style: { color: iconColor } }}
                  InputProps={{ style: { color: iconColor } }}
                />
                <TextField
                  fullWidth
                  label="Overall Drawdown (%)"
                  type="number"
                  value={stage.rules.maxOverallDrawdown || ''}
                  onChange={(e) => handleCustomStageChange(index, 'maxOverallDrawdown', e.target.value)}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ style: { color: iconColor } }}
                  InputProps={{ style: { color: iconColor } }}
                />
                <TextField
                  fullWidth
                  label="Min Trading Days"
                  type="number"
                  value={stage.rules.minTradingDays || ''}
                  onChange={(e) => handleCustomStageChange(index, 'minTradingDays', e.target.value)}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ style: { color: iconColor } }}
                  InputProps={{ style: { color: iconColor } }}
                />
                <TextField
                  fullWidth
                  label="Max Trading Days"
                  type="number"
                  value={stage.rules.maxTradingDays || ''}
                  onChange={(e) => handleCustomStageChange(index, 'maxTradingDays', e.target.value)}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ style: { color: iconColor } }}
                  InputProps={{ style: { color: iconColor } }}
                />
                <TextField
                  fullWidth
                  label="Consistency (%)"
                  type="number"
                  value={stage.rules.consistency || ''}
                  onChange={(e) => handleCustomStageChange(index, 'consistency', e.target.value)}
                  sx={{ mb: 1 }}
                  InputLabelProps={{ style: { color: iconColor } }}
                  InputProps={{ style: { color: iconColor } }}
                />
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel sx={{ color: iconColor }}>Hold Weekend</InputLabel>
                  <Select
                    value={stage.rules.holdWeekend !== undefined ? stage.rules.holdWeekend.toString() : 'true'}
                    onChange={(e) => handleCustomStageChange(index, 'holdWeekend', e.target.value)}
                    sx={{ color: iconColor }}
                    label="Hold Weekend"
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ))}
            <MDButton
              variant="contained"
              color="success"
              onClick={handleSaveCustomChallenge}
              sx={{ mt: 1 }}
            >
              Save Custom Challenge
            </MDButton>
          </Box>
        )}
        <MDButton
          variant="outlined"
          color="warning"
          onClick={handleAddCustomChallenge}
          sx={{ mt: 2 }}
          startIcon={<AddIcon />}
        >
          Add Custom Challenge
        </MDButton>
        <MDButton
          variant="outlined"
          color="error"
          onClick={() => {
            setOpenModal(false);
            onCloseChallengeModal();
          }}
          sx={{ mt: 2, ml: 2 }}
        >
          Cancel
        </MDButton>
      </Box>
    </Modal>
  );

  const renderTrackingCard = () => (
    <Box>
      {selectedChallenges.map(challenge => {
        const currentStage = challenge.currentStage || 1;
        const stageRules = challenge.stages.find(s => s.stage === currentStage)?.rules || {};
        const metrics = challengeMetrics[challenge.id] || calculateMetrics(challenge);
        const isProfitTargetMet = metrics.profitTarget >= stageRules.profitTarget;
        const isDailyDrawdownMet = Math.abs(metrics.dailyDrawdown) <= stageRules.maxDailyDrawdown;
        const isOverallDrawdownMet = Math.abs(metrics.overallDrawdown) <= stageRules.maxOverallDrawdown;
        const isMinTradingDaysMet = metrics.tradingDays >= stageRules.minTradingDays;
        const isMaxTradingDaysMet = stageRules.maxTradingDays === null || metrics.tradingDays <= stageRules.maxTradingDays;
        const isConsistencyMet = stageRules.consistency === null || metrics.consistency <= stageRules.consistency;
        const isStageComplete = isProfitTargetMet && isDailyDrawdownMet && isOverallDrawdownMet && isMinTradingDaysMet && isMaxTradingDaysMet && isConsistencyMet;
        const isChallengeComplete = challenge.status === 'completed' || (isStageComplete && currentStage === challenge.stages.length);
        const isChallengeFailed = challenge.status === 'failed';
  
        console.log(`Challenge ${challenge.id} status:`, {
          currentStage,
          isStageComplete,
          isLastStage: currentStage === challenge.stages.length,
          isChallengeComplete,
          isChallengeFailed,
          metrics,
          stageRules
        });
  
        return (
          <Card
            key={challenge.id}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: '12px',
              bgcolor: theme.palette.background.default,
              boxShadow: `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'}`,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="caption" fontWeight="medium">
                  {challenge.name} {isChallengeComplete ? '(Completed)' : isChallengeFailed ? '(Failed)' : `(Stage ${currentStage})`}
                </MDTypography>
                <IconButton
                  onClick={() => handleRemoveChallenge(challenge.id)}
                  sx={{ color: iconColor }}
                  disabled={isChallengeComplete || isChallengeFailed}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              {isChallengeComplete ? (
                <Box
                  sx={{
                    borderRadius: '12px',
                    px: 2,
                    py: 1,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.15)',
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.5)' : 'rgba(76, 175, 80, 0.3)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <CheckCircle sx={{ color: theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32', mr: 1 }} />
                  <MDTypography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32' }}>
                    Congratulations! You would have passed the {challenge.name} challenge!
                  </MDTypography>
                </Box>
              ) : isChallengeFailed ? (
                <Box
                  sx={{
                    borderRadius: '12px',
                    px: 2,
                    py: 1,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.15)',
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.5)' : 'rgba(244, 67, 54, 0.3)'}`,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                <ErrorIcon sx={{ color: theme.palette.mode === 'dark' ? '#ef5350' : '#c62828', mr: 1 }} />
                <MDTypography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#ef5350' : '#c62828' }}>
                  Challenge Failed: Exceeded drawdown limits for {challenge.name}.
                </MDTypography>
              </Box>
              ) : (
                // Existing metrics display code remains unchanged
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <MDTypography variant="caption">
                      Profit Target:
                    </MDTypography>
                    <Box
                      sx={{
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        backgroundColor: isProfitTargetMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.3)'
                            : 'rgba(76, 175, 80, 0.15)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.3)'
                            : 'rgba(244, 67, 54, 0.15)',
                        border: `1px solid ${isProfitTargetMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.5)'
                            : 'rgba(76, 175, 80, 0.3)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.5)'
                            : 'rgba(244, 67, 54, 0.3)'}`,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          color: isProfitTargetMet
                            ? theme.palette.mode === 'dark'
                              ? '#81c784'
                              : '#2e7d32'
                            : theme.palette.mode === 'dark'
                              ? '#ef5350'
                              : '#c62828',
                        }}
                      >
                        {metrics.profitTarget.toFixed(2)}% / {stageRules.profitTarget}%
                      </MDTypography>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <MDTypography variant="caption">
                      Daily Drawdown:
                    </MDTypography>
                    <Box
                      sx={{
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        backgroundColor: isDailyDrawdownMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.3)'
                            : 'rgba(76, 175, 80, 0.15)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.3)'
                            : 'rgba(244, 67, 54, 0.15)',
                        border: `1px solid ${isDailyDrawdownMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.5)'
                            : 'rgba(76, 175, 80, 0.3)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.5)'
                            : 'rgba(244, 67, 54, 0.3)'}`,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          color: isDailyDrawdownMet
                            ? theme.palette.mode === 'dark'
                              ? '#81c784'
                              : '#2e7d32'
                            : theme.palette.mode === 'dark'
                              ? '#ef5350'
                              : '#c62828',
                        }}
                      >
                        {metrics.dailyDrawdown.toFixed(2)}% / {stageRules.maxDailyDrawdown}%
                      </MDTypography>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <MDTypography variant="caption">
                      Max Drawdown:
                    </MDTypography>
                    <Box
                      sx={{
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        backgroundColor: isOverallDrawdownMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.3)'
                            : 'rgba(76, 175, 80, 0.15)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.3)'
                            : 'rgba(244, 67, 54, 0.15)',
                        border: `1px solid ${isOverallDrawdownMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.5)'
                            : 'rgba(76, 175, 80, 0.3)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.5)'
                            : 'rgba(244, 67, 54, 0.3)'}`,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          color: isOverallDrawdownMet
                            ? theme.palette.mode === 'dark'
                              ? '#81c784'
                              : '#2e7d32'
                            : theme.palette.mode === 'dark'
                              ? '#ef5350'
                              : '#c62828',
                        }}
                      >
                        {metrics.overallDrawdown.toFixed(2)}% / {stageRules.maxOverallDrawdown}%
                      </MDTypography>
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <MDTypography variant="caption">
                      Trading Days:
                    </MDTypography>
                    <Box
                      sx={{
                        borderRadius: '12px',
                        px: 1,
                        py: 0.25,
                        backgroundColor: isMinTradingDaysMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.3)'
                            : 'rgba(76, 175, 80, 0.15)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.3)'
                            : 'rgba(244, 67, 54, 0.15)',
                        border: `1px solid ${isMinTradingDaysMet
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.5)'
                            : 'rgba(76, 175, 80, 0.3)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.5)'
                            : 'rgba(244, 67, 54, 0.3)'}`,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        sx={{
                          color: isMinTradingDaysMet
                            ? theme.palette.mode === 'dark'
                              ? '#81c784'
                              : '#2e7d32'
                            : theme.palette.mode === 'dark'
                              ? '#ef5350'
                              : '#c62828',
                        }}
                      >
                        {metrics.tradingDays} / {stageRules.minTradingDays}
                      </MDTypography>
                    </Box>
                  </Box>
                  {stageRules.consistency !== null && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <MDTypography variant="caption">
                        Consistency:
                      </MDTypography>
                      <Box
                        sx={{
                          borderRadius: '12px',
                          px: 1,
                          py: 0.25,
                          backgroundColor: isConsistencyMet
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(76, 175, 80, 0.3)'
                            : 'rgba(76, 175, 80, 0.15)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.3)'
                            : 'rgba(244, 67, 54, 0.15)',
                          border: `1px solid ${isConsistencyMet
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(76, 175, 80, 0.5)'
                            : 'rgba(76, 175, 80, 0.3)'
                          : theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.5)'
                            : 'rgba(244, 67, 54, 0.3)'}`,
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          fontWeight="medium"
                          sx={{
                            color: isConsistencyMet
                              ? theme.palette.mode === 'dark'
                                ? '#81c784'
                                : '#2e7d32'
                              : theme.palette.mode === 'dark'
                                ? '#ef5350'
                                : '#c62828',
                          }}
                        >
                          {metrics.consistency.toFixed(2)}% / {stageRules.consistency}%
                        </MDTypography>
                      </Box>
                    </Box>
                  )}
                  {isStageComplete && !isChallengeComplete && challenge.stages.find(s => s.stage === currentStage + 1) && (
                    <MDButton
                      variant="contained"
                      color="warning"
                      onClick={() => setStageDialog({ open: true, challengeId: challenge.id, nextStage: currentStage + 1 })}
                      sx={{ mt: 2 }}
                    >
                      Proceed to Next Stage
                    </MDButton>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  return (
    <>
      {renderSelectionModal()}
      {isPanelOpen && selectedChallenges.length > 0 && (
        <Box mt={2}>
          <MDTypography variant="h6" mb={2}>
            Prop Firm Challenges
          </MDTypography>
          {renderTrackingCard()}
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Modal
        open={stageDialog.open}
        onClose={() => setStageDialog({ open: false, challengeId: null, nextStage: null })}
        sx={{ zIndex: 1300 }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: theme.palette.background.default,
            borderRadius: '12px',
            boxShadow: 1,
            p: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <MDTypography variant="h6" mb={2}>
            Confirm Stage Transition
          </MDTypography>
          <MDTypography variant="body2" mb={2}>
            Proceed to stage {stageDialog.nextStage}?
          </MDTypography>
          <MDButton
            variant="contained"
            color="warning"
            onClick={() => handleStageTransition(stageDialog.challengeId, stageDialog.nextStage)}
            sx={{ mr: 1 }}
          >
            Confirm
          </MDButton>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setStageDialog({ open: false, challengeId: null, nextStage: null })}
          >
            Cancel
          </MDButton>
        </Box>
      </Modal>
    </>
  );
};

export default PropFirmChallenge;